import { Button, buttonClasses, Menu, MenuItem, Stack, styled, Switch, Typography, CircularProgress, switchClasses } from "@mui/material";
import React, { useState, useEffect } from "react";
import Tooltip from "@hyro-components/Tooltip";
import RefreshIcon from "src/icons/RefreshIcon";

const BUTTON_SIZE = "36px";
const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "autoRefresh"
})(({ theme, disabled, autoRefresh }) => ({
  backgroundColor: theme.palette.system[0],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(0.75),
  height: BUTTON_SIZE,
  [`& .${buttonClasses.startIcon}`]: {
    margin: 0
  },
  minWidth: BUTTON_SIZE,
  width: BUTTON_SIZE,
  color: theme.palette.system[700],
  border: `1px solid ${disabled ? theme.palette.system[200] : theme.palette.system[400]}`,
  "& svg path": {
    stroke: "currentColor"
  },
  "&:hover, &.active": {
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.tertiary[100],
    borderColor: theme.palette.secondary[500],
  },
  ...(autoRefresh && {
    color: theme.palette.primary[500],
    backgroundColor: theme.palette.tertiary[100],
    borderColor: theme.palette.secondary[500],
  })
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  padding: "12px"
}));

const AutoRefreshContainer = styled(Stack)(({ theme }) => ({
  padding: "12px"
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  [`& .${switchClasses.thumb}`]: {
    boxShadow: theme.shadows[4]
  },
}));

const RefreshButton = ({ onClick, isLoading, autoRefreshInterval = 30000, isRefreshing, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const isMenuOpen = Boolean(anchorEl);
  const icon = isRefreshing ? (
    <CircularProgress size={20} thickness={4} sx={{ color: "inherit" }} />
  ) : (
    <RefreshIcon disabled={isLoading} />
  );

  useEffect(() => {
    let intervalId;

    if (autoRefresh) {
      // A backgrounded tab would otherwise keep polling indefinitely. Only the automatic tick is
      // skipped — an explicit click always refreshes.
      intervalId = setInterval(() => {
        if (document.visibilityState !== "visible") return;
        onClick({ manual: false });
      }, autoRefreshInterval);
    }

    return () => { intervalId && clearInterval(intervalId); };
  }, [autoRefresh, onClick, autoRefreshInterval]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAutoRefreshToggle = (event) => {
    setAutoRefresh(!autoRefresh);
  };

  // Consumers can tell an explicit click ({ manual: true }) from an auto-refresh tick ({ manual: false }).
  const handleManualRefresh = () => {
    onClick({ manual: true });
    handleMenuClose();
  };

  return (
    <>
      <Tooltip title="Refresh data" testId="test-refresh-button-tooltip" tooltipStyle={{ maxWidth: "120px" }}>
        <StyledButton
          data-testid="test-refresh-button"
          disabled={isLoading || disabled}
          onClick={handleMenuOpen}
          startIcon={icon}
          className={isMenuOpen ? "active" : ""}
          autoRefresh={autoRefresh}
        />
      </Tooltip>

      <Menu data-testid="test-refresh-button-menu" anchorEl={anchorEl} open={isMenuOpen} onClose={handleMenuClose} >
        <AutoRefreshContainer>
          <Stack direction="row" alignItems="center">
            <StyledSwitch
              data-testid="test-refresh-button-auto-refresh-switch"
              checked={autoRefresh}
              onChange={handleAutoRefreshToggle}
              size="small"
            />
            <Typography variant="body2" data-testid="test-refresh-button-auto-refresh-label">Auto-refresh {autoRefreshInterval / 1000}s</Typography>
          </Stack>
        </AutoRefreshContainer>
        <StyledMenuItem onClick={handleManualRefresh} disabled={isLoading} data-testid="test-refresh-button-manual-refresh-item">
          <Typography variant="body2">Manual Refresh</Typography>
        </StyledMenuItem>
      </Menu>
    </>
  );
};

export default RefreshButton;
